import { type ReactNode, type RefObject, useState } from 'react';

import { DateTimePicker } from '@/components';
import AddButton from '@/components/Button/AddButton';
import { Input } from '@/components/Inputs';
import type { TaskStatus } from '@/store/tasks/tasks.dto';

import { AiGenerating } from '../AiGenerating';
import type { AiChatMessage, TaskItem, TaskSuggestion } from '../eventWizard.types';
import { fmtRange, fromIsoValue, toIsoValue } from '../eventWizard.utils';
import s from '../EventWizardModal.module.scss';

import { TaskFormModal } from './TaskFormModal';
import { TasksAiPanel } from './TasksAiPanel';

interface TasksTabProps {
  planTabs: ReactNode;
  eventStart: Date | null;
  eventEnd: Date | null;
  eventTitle: string;
  tasks: TaskItem[];
  onRemoveTask: (id: number) => void;
  tasksMode: string;
  tasksAiLoading: boolean;

  quickTaskTitle: string;
  onQuickTaskTitleChange: (value: string) => void;
  quickTaskDate: Date | null;
  quickTaskEndDate: Date | null;
  onQuickTaskDatesChange: (start: Date | null, end: Date | null) => void;
  onAddQuickTask: () => void;

  showTaskForm: boolean;
  onOpenTaskForm: () => void;
  onCloseTaskForm: () => void;
  taskStep: number;
  onTaskStepChange: (step: number) => void;
  taskTitle: string;
  onTaskTitleChange: (value: string) => void;
  taskPrivate: boolean;
  onTaskPrivateToggle: () => void;
  taskStatus: TaskStatus;
  onTaskStatusChange: (value: TaskStatus) => void;
  taskType: string;
  onTaskTypeChange: (value: string) => void;
  taskDate: Date | null;
  taskEndDate: Date | null;
  onTaskDatesChange: (start: Date | null, end: Date | null) => void;
  taskEquipment: string;
  onTaskEquipmentChange: (value: string) => void;
  taskDescription: string;
  onTaskDescriptionChange: (value: string) => void;
  taskRepeat: string;
  onTaskRepeatChange: (value: string) => void;
  onSaveTask: () => void;

  aiPanelOpen: boolean;
  onToggleAiPanel: () => void;
  aiSuggestions: TaskSuggestion[];
  aiChat: AiChatMessage[];
  aiChatRef: RefObject<HTMLDivElement | null>;
  aiThinking: boolean;
  aiPromptDraft: string;
  onAiPromptDraftChange: (value: string) => void;
  onSubmitAiPrompt: () => void;
  editTaskId: number | null;
  editTaskText: string;
  onEditTaskTextChange: (value: string) => void;
  onStartEditTask: (id: number, text: string) => void;
  onSaveEditTask: () => void;
  onCancelEditTask: () => void;
  onAcceptSuggestion: (id: number) => void;
  onRejectSuggestion: (id: number) => void;
  onAcceptAllSuggestions: () => void;
}

export function TasksTab({
  planTabs,
  eventStart,
  eventEnd,
  eventTitle,
  tasks,
  onRemoveTask,
  tasksMode,
  tasksAiLoading,
  quickTaskTitle,
  onQuickTaskTitleChange,
  quickTaskDate,
  quickTaskEndDate,
  onQuickTaskDatesChange,
  onAddQuickTask,
  showTaskForm,
  onOpenTaskForm,
  onCloseTaskForm,
  taskStep,
  onTaskStepChange,
  taskTitle,
  onTaskTitleChange,
  taskPrivate,
  onTaskPrivateToggle,
  taskStatus,
  onTaskStatusChange,
  taskType,
  onTaskTypeChange,
  taskDate,
  taskEndDate,
  onTaskDatesChange,
  taskEquipment,
  onTaskEquipmentChange,
  taskDescription,
  onTaskDescriptionChange,
  taskRepeat,
  onTaskRepeatChange,
  onSaveTask,
  aiPanelOpen,
  onToggleAiPanel,
  aiSuggestions,
  aiChat,
  aiChatRef,
  aiThinking,
  aiPromptDraft,
  onAiPromptDraftChange,
  onSubmitAiPrompt,
  editTaskId,
  editTaskText,
  onEditTaskTextChange,
  onStartEditTask,
  onSaveEditTask,
  onCancelEditTask,
  onAcceptSuggestion,
  onRejectSuggestion,
  onAcceptAllSuggestions,
}: TasksTabProps) {
  const [dateFieldKey, setDateFieldKey] = useState(0);

  return (
    <div className={s.fields}>
      {!showTaskForm && tasksAiLoading && <AiGenerating hint="Drafting your tasks" lines={0} />}

      <TaskFormModal
        open={showTaskForm}
        onClose={onCloseTaskForm}
        eventTitle={eventTitle}
        taskStep={taskStep}
        onTaskStepChange={onTaskStepChange}
        taskTitle={taskTitle}
        onTaskTitleChange={onTaskTitleChange}
        taskPrivate={taskPrivate}
        onTaskPrivateToggle={onTaskPrivateToggle}
        taskStatus={taskStatus}
        onTaskStatusChange={onTaskStatusChange}
        taskType={taskType}
        onTaskTypeChange={onTaskTypeChange}
        taskDate={taskDate}
        taskEndDate={taskEndDate}
        onTaskDatesChange={onTaskDatesChange}
        taskEquipment={taskEquipment}
        onTaskEquipmentChange={onTaskEquipmentChange}
        taskDescription={taskDescription}
        onTaskDescriptionChange={onTaskDescriptionChange}
        taskRepeat={taskRepeat}
        onTaskRepeatChange={onTaskRepeatChange}
        onSave={onSaveTask}
        eventStart={eventStart}
        eventEnd={eventEnd}
      />

      {!showTaskForm && planTabs}

      {!showTaskForm &&
        tasksMode === 'ai' &&
        !tasksAiLoading &&
        (aiSuggestions.length > 0 || aiChat.length > 0) && (
          <TasksAiPanel
            open={aiPanelOpen}
            onToggle={onToggleAiPanel}
            suggestions={aiSuggestions}
            chat={aiChat}
            chatRef={aiChatRef}
            thinking={aiThinking}
            promptDraft={aiPromptDraft}
            onPromptDraftChange={onAiPromptDraftChange}
            onSubmitPrompt={onSubmitAiPrompt}
            editId={editTaskId}
            editText={editTaskText}
            onEditTextChange={onEditTaskTextChange}
            onStartEdit={onStartEditTask}
            onSaveEdit={onSaveEditTask}
            onCancelEdit={onCancelEditTask}
            onAccept={onAcceptSuggestion}
            onReject={onRejectSuggestion}
            onAcceptAll={onAcceptAllSuggestions}
          />
        )}

      {!showTaskForm && (
        <div className={s.taskQuickRow}>
          <DateTimePicker
            key={dateFieldKey}
            label="Dates"
            placeholder="Choose dates"
            minDate={eventStart ?? undefined}
            maxDate={eventEnd ?? undefined}
            value={toIsoValue(quickTaskDate)}
            endValue={toIsoValue(quickTaskEndDate)}
            onRangeChange={(start, end) =>
              onQuickTaskDatesChange(fromIsoValue(start), fromIsoValue(end))
            }
          />
          <div className={s.inlineField}>
            <Input
              label="Task"
              value={quickTaskTitle}
              onChange={(e) => onQuickTaskTitleChange(e.target.value)}
              placeholder="Check the tender fuel"
            />
          </div>
        </div>
      )}

      {!showTaskForm && (
        <button type="button" className={s.addMoreBtn} onClick={onOpenTaskForm}>
          <span className={s.addMoreBtnIcon}>+</span>
          <span>Add Task Details</span>
        </button>
      )}

      {!showTaskForm && (
        <AddButton
          label="Task"
          onClick={() => {
            onAddQuickTask();
            // The range field keeps its own step; remount it so it starts over, folded, with the cleared dates.
            setDateFieldKey((k) => k + 1);
          }}
          disabled={!quickTaskTitle.trim()}
        />
      )}

      {!showTaskForm && tasks.length > 0 && !tasksAiLoading && (
        <div className={s.rowList}>
          <div className={s.listCount}>
            {tasks.length} task{tasks.length !== 1 ? 's' : ''} added
          </div>
          {tasks.map((task) => (
            <div key={task.id} className={s.taskRow}>
              <span className={s.taskCheck}>☐</span>
              <span className={s.taskTitle}>{task.title}</span>
              {(task.start || task.end) && (
                <span className={s.taskWhen}>{fmtRange(task.start, task.end)}</span>
              )}
              <button
                type="button"
                className={s.removeBtn}
                onClick={() => onRemoveTask(task.id)}
                aria-label="Remove task"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
